import React, { useState, useEffect } from 'react';
import { Palette, Building, Eye, Crown, Users } from 'lucide-react';
import { useAuth } from '../../AuthProvider';
import { TeamBrandingSelector } from './TeamBrandingSelector';

interface BrandingSelectorProps {
  value: 'beyondpages' | 'none' | 'custom';
  onChange: (value: 'beyondpages' | 'none' | 'custom') => void;
  disabled?: boolean;
  selectedTeamBrandingId?: string;
  onTeamBrandingSelect?: (brandingId: string | null) => void;
}

interface BrandingAccess {
  hasAccess: boolean;
  plan: string;
  message?: string;
}

export const BrandingSelector: React.FC<BrandingSelectorProps> = ({ 
  value, 
  onChange, 
  disabled = false,
  selectedTeamBrandingId,
  onTeamBrandingSelect
}) => {
  const { session, user } = useAuth();
  const [access, setAccess] = useState<BrandingAccess | null>(null);
  const [loading, setLoading] = useState(true);
  
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  useEffect(() => {
    checkBrandingAccess();
  }, [session]);

  const checkBrandingAccess = async () => {
    if (!session?.access_token) return;

    console.log('🎨 BRANDING DEBUG - BrandingSelector checking access...');
    try {
      const response = await fetch(`${API_BASE}/api/branding/check-access`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      console.log('🎨 BRANDING DEBUG - Access check response status:', response.status);

      if (response.ok) {
        const accessData = await response.json();
        console.log('🎨 BRANDING DEBUG - Access check result:', accessData);
        setAccess(accessData);
        
        // If user doesn't have access and custom is selected, switch to beyondpages
        if (!accessData.hasAccess && value === 'custom') {
          console.log('🎨 BRANDING DEBUG - User has no access, switching from custom to beyondpages');
          onChange('beyondpages');
        }
      } else {
        console.log('🎨 BRANDING DEBUG - Access check failed with status:', response.status);
      }
    } catch (error) {
      console.error('🎨 BRANDING DEBUG - Error checking branding access:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // If user doesn't have white-label access, show upgrade prompt
  if (access && !access.hasAccess) {
    return (
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Crown className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                White-Label Branding verfügbar ab Business Plan
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Mit White-Label können Sie Ihr eigenes Branding verwenden oder das BeyondPages-Branding entfernen.
              </p>
              <a
                href="/qr/pricing"
                className="inline-flex items-center text-xs text-amber-800 hover:text-amber-900 font-medium mt-2"
              >
                Jetzt upgraden →
              </a>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p className="text-sm text-gray-600">
            <Building className="inline-block w-4 h-4 mr-1" />
            Ihre QR-Seiten zeigen: <strong>Powered by BeyondPages</strong>
          </p>
        </div>
      </div>
    );
  }

  // User has white-label access
  return (
    <div className="space-y-2">
      {/* BeyondPages Branding */}
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name="branding"
          value="beyondpages"
          checked={value === 'beyondpages'}
          onChange={() => {
            if (!disabled) {
              console.log('🎨 BRANDING DEBUG - User selected: beyondpages');
              onChange('beyondpages');
            }
          }}
          disabled={disabled}
          className="mr-3 text-blue-600 focus:ring-blue-500"
        />
        <div>
          <span className="text-sm font-medium text-gray-900">BeyondPages Branding</span>
          <div className="text-xs text-gray-500">Zeigt "Powered by BeyondPages" im Footer</div>
        </div>
      </label>

      {/* Eigenes Branding */}
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name="branding"
          value="custom"
          checked={value === 'custom'}
          onChange={() => {
            if (!disabled) {
              console.log('🎨 BRANDING DEBUG - User selected: custom');
              onChange('custom');
            }
          }}
          disabled={disabled}
          className="mr-3 text-blue-600 focus:ring-blue-500"
        />
        <div>
          <span className="text-sm font-medium text-gray-900">Eigenes Branding</span>
          <div className="text-xs text-gray-500">Verwendet Ihr Logo und Firmenname</div>
        </div>
      </label>

      {/* Kein Branding */}
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name="branding"
          value="none"
          checked={value === 'none'}
          onChange={() => {
            if (!disabled) {
              console.log('🎨 BRANDING DEBUG - User selected: none');
              onChange('none');
            }
          }}
          disabled={disabled}
          className="mr-3 text-blue-600 focus:ring-blue-500"
        />
        <div>
          <span className="text-sm font-medium text-gray-900">Kein Branding</span>
          <div className="text-xs text-gray-500">Zeigt keinen Footer auf der QR-Seite</div>
        </div>
      </label>

      
      {/* Preview hint */}
      {value === 'custom' && (
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mt-3">
          <p className="text-xs text-blue-700">
            <Eye className="inline-block w-3 h-3 mr-1" />
            Ihr Branding wird aus Ihren <a href="/qr/dash/profile#branding" className="underline font-medium">Profileinstellungen</a> übernommen.
          </p>
        </div>
      )}

      {/* Team Branding Selector */}
      {value === 'custom' && user && onTeamBrandingSelect && (
        <div className="mt-4">
          <TeamBrandingSelector
            selectedBrandingId={selectedTeamBrandingId}
            onSelect={onTeamBrandingSelect}
            currentUserId={user.id}
          />
        </div>
      )}
    </div>
  );
}; 